import Cookies from "js-cookie";
import {create} from "zustand";
import {config} from "@/constants/config";
import {sessionGet, sessionRemove, sessionSet} from "@/util/sessionStorage";
import {decodeMyToken, isTokenExpired} from "./util.token";

export type AuthStatus = "checking" | "authenticated" | "unauthenticated";

export interface AuthUser {
  id?: string | number;
  email?: string;
  name?: string;
  [key: string]: unknown;
}

interface AuthState {
  status: AuthStatus;
  token: string | null;
  user: AuthUser | null;
  setSession: (payload: {token: string; user: AuthUser}) => void;
  clearSession: () => void;
  hydrate: () => void;
}

interface PersistedSession {
  token: string;
  user: AuthUser;
}

export const useAuthStore = create<AuthState>(set => ({
  status: "checking",
  token: null,
  user: null,

  setSession: ({token, user}) => {
    sessionSet<PersistedSession>(config.sessionKey, {token, user});
    set({status: "authenticated", token, user});
  },

  clearSession: () => {
    sessionRemove(config.sessionKey);
    set({status: "unauthenticated", token: null, user: null});
  },

  hydrate: () => {
    // 1. Check new sessionStorage-based session
    const persisted = sessionGet<PersistedSession>(config.sessionKey);
    if (persisted?.token) {
      set({status: "authenticated", token: persisted.token, user: persisted.user});
      return;
    }

    // 2. Fallback: read the legacy cookie token written by the old auth store (js-cookie "token")
    try {
      const raw = Cookies.get("token");
      if (raw) {
        const token = JSON.parse(raw) as string;
        const payload = decodeMyToken(token);
        if (payload && !isTokenExpired(payload)) {
          set({status: "authenticated", token, user: payload as AuthUser});
          return;
        }
      }
    } catch {
      // ignore malformed cookie
    }

    set({status: "unauthenticated", token: null, user: null});
  },
}));
